import { jsxs, jsx } from 'react/jsx-runtime';
import { useId, useRef, useLayoutEffect } from 'react';
import { CircleInfoIcon } from '../../../icons/CircleInfoIcon.js';
import { CircleExclamationIcon } from '../../../icons/CircleExclamationIcon.js';
import { CircleTickIcon } from '../../../icons/CircleTickIcon.js';
import { AUTOFILL_INPUT_CLASS, autofillOverrideStyle } from '../../../constants/autofillOverrideStyle.js';

const styles = {
    container: 'w-full',
    inputWrapper: 'relative',
    inputLabel: 'block text-body-strong text-text-body mb-space-200',
    input: 'w-full h-12 px-space-400 py-space-200 bg-input-background-default rounded-radius-100 disabled:bg-input-background-disabled border border-input-border-default disabled:border-input-border-disabled focus:border-border-focus-2 focus:border-3 focus:outline-none [&[type="number"]::-webkit-inner-spin-button]:hidden [&[type="number"]::-webkit-outer-spin-button]:hidden',
    inputSuccess: 'border-input-border-success',
    inputError: 'border-input-border-error',
    leftIconWrapper: 'absolute left-space-400 top-1/2 -translate-y-1/2 flex items-center',
    rightIconWrapper: 'absolute right-space-400 top-1/2 -translate-y-1/2 flex items-center',
    hintTextContainer: 'mt-space-200 flex gap-space-100',
    hintText: 'text-body-small text-text-body',
    errorText: 'text-body-small text-(--text-error-color)',
    successText: 'text-body-small text-(--text-success-color)',
    hintIconContainer: 'flex items-center h-(--line-height-body-small)',
};
const Input = ({ id, name, type = 'text', value, defaultValue, placeholder, disabled = false, required = false, readOnly = false, autoComplete, label, hideLabel = false, leftIcon, rightIcon, hintText, hasError, hasSuccess, onChange, onBlur, onFocus, ref, ...rest }) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hintId = `${inputId}-hint`;
    const errorId = `${inputId}-error`;
    const successId = `${inputId}-success`;
    const leftIconRef = useRef(null);
    const rightIconRef = useRef(null);
    const internalInputRef = useRef(null);
    useLayoutEffect(() => {
        const inputElement = internalInputRef.current;
        if (!inputElement)
            return;
        const leftIconWidth = leftIconRef.current?.firstElementChild?.getBoundingClientRect().width ||
            leftIconRef.current?.offsetWidth ||
            0;
        const rightIconWidth = rightIconRef.current?.firstElementChild?.getBoundingClientRect().width ||
            rightIconRef.current?.offsetWidth ||
            0;
        const defaultPadding = 16;
        const leftPadding = leftIconWidth ? leftIconWidth + defaultPadding * 1.5 : defaultPadding;
        const rightPadding = rightIconWidth ? rightIconWidth + defaultPadding * 1.5 : defaultPadding;
        inputElement.style.paddingLeft = `${leftPadding}px`;
        inputElement.style.paddingRight = `${rightPadding}px`;
    }, [leftIcon, rightIcon]);
    const describedBy = [
        hintText && !hasError && !hasSuccess && hintId,
        !!hasError && errorId,
        !!hasSuccess && !hasError && successId,
    ]
        .filter(Boolean)
        .join(' ');
    return (jsxs("div", { className: styles.container, children: [label && (jsx("label", { htmlFor: inputId, className: `${styles.inputLabel} ${hideLabel ? 'sr-only' : ''}`, children: label })), jsxs("div", { className: styles.inputWrapper, children: [leftIcon && (jsx("div", { ref: leftIconRef, className: styles.leftIconWrapper, children: leftIcon })), jsx("input", { ref: (node) => {
                            internalInputRef.current = node;
                            if (typeof ref === 'function') {
                                ref(node);
                            }
                            else if (ref) {
                                ref.current = node;
                            }
                        }, id: inputId, name: name, type: type, value: value, defaultValue: defaultValue, placeholder: placeholder, disabled: disabled, required: required, readOnly: readOnly, autoComplete: autoComplete, onChange: onChange, onBlur: onBlur, onFocus: onFocus, "aria-invalid": !!hasError, "aria-describedby": describedBy, "aria-required": required, className: `
            ${styles.input}
            ${AUTOFILL_INPUT_CLASS}
            ${hasError && styles.inputError}
            ${hasSuccess && !hasError && styles.inputSuccess}
          `, ...rest }), jsx("style", { children: autofillOverrideStyle }), rightIcon && (jsx("div", { ref: rightIconRef, className: styles.rightIconWrapper, children: rightIcon }))] }), hintText &&
                (!hasError || typeof hasError !== 'string') &&
                (!hasSuccess || typeof hasSuccess !== 'string') && (jsxs("div", { className: styles.hintTextContainer, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleInfoIcon, {}) }), jsx("p", { id: hintId, className: styles.hintText, children: hintText })] })), hasError && typeof hasError === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-error`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleExclamationIcon, {}) }), jsx("p", { id: errorId, className: styles.errorText, role: "alert", children: hasError })] })), hasSuccess && !hasError && typeof hasSuccess === 'string' && (jsxs("div", { className: `${styles.hintTextContainer} text-text-success`, children: [jsx("div", { className: styles.hintIconContainer, children: jsx(CircleTickIcon, {}) }), jsx("p", { id: successId, className: styles.successText, children: hasSuccess })] }))] }));
};

export { Input };
